import { useMutation } from "@tanstack/react-query";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { backendService } from "../../../services/backend.service";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import { SettingRow } from "../../UIToolkit/molecules/SettingRow";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";

/**
 * Mounted with a per-department `key` by the parent, so switching departments re-seeds the input
 * from freshly-mounted state — no syncing effect needed (our own save keeps the value current).
 */
export const AgencyShiftSettings: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment, setChosenDepartment, showErrorSnackbar } = useHospitalData();
	const savedDays = chosenDepartment.agencyScheduledWithoutPunchDays ?? 0;
	const [draft, setDraft] = useState(String(savedDays));

	const mutation = useMutation({
		mutationFn: (value: number) => backendService.setAgencyScheduledWithoutPunchDays(chosenDepartment.department_id, value),
		throwOnError: false,
		onSuccess: (_data, value) => {
			setChosenDepartment((prev) => ({ ...prev, agencyScheduledWithoutPunchDays: value }));
		},
		onError: (error) => {
			showErrorSnackbar(t("agency.config.scheduledWithoutPunch.saveError"), {}, error);
			setDraft(String(savedDays));
		},
	});

	const commit = () => {
		const parsed = Number(draft);
		// Revert invalid input rather than persisting it.
		if (draft.trim() === "" || !Number.isInteger(parsed) || parsed < 0) {
			setDraft(String(savedDays));
			return;
		}
		if (parsed !== savedDays) {
			mutation.mutate(parsed);
		}
	};

	return (
		<SettingsSection
			title={t("agency.config.scheduledWithoutPunch.title")}
			description={t("agency.config.scheduledWithoutPunch.description")}
			headerPlacement="side"
			divider
			dataTestId="agency-shift-settings"
		>
			<SettingRow
				label={t("agency.config.scheduledWithoutPunch.label")}
				description={t("agency.config.scheduledWithoutPunch.helper")}
				control={
					<SystemValidationTextField
						inputType="number"
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						// fullWidth here fills the fixed-width FormControl; SystemValidationTextField's own
						// `fullWidth` prop only widens the wrapper, not the inner input.
						injectedProps={{ fullWidth: true, onBlur: commit }}
						formControlSx={{ width: 160 }}
						inputProps={{ inputProps: { min: 0 } }}
						disabled={mutation.isPending}
						dataTestId="agency-scheduled-without-punch-days-input"
					/>
				}
			/>
		</SettingsSection>
	);
};
